import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AuthService, EventsService, PaymentV2Service, UtilService, VariablesService, VwoService } from '../../shared/services';
import { IPaymentSipConfig } from '../payment-sip/payment-sip.model';
import { AMOUNT_FORM } from '../payment/objects/amount.form';
import { GCaptchaComponent } from '../g-captcha/g-captcha.component';
import { IUser } from 'src/app/shared/models';

@Component({
  selector: 'app-payment-sip-form',
  templateUrl: './payment-sip-form.component.html',
  styleUrls: ['./payment-sip-form.component.scss']
})
export class PaymentSipFormComponent implements OnInit, OnChanges {
  readonly vars = inject(VariablesService);

  @Input() config: IPaymentSipConfig;
  @Output() dataEmit = new EventEmitter();
  @Output() setCurrency = new EventEmitter();
  @Output() formSubmitted = new EventEmitter();
  donationForm: FormGroup;
  nameControl = new FormControl('', [Validators.required, Validators.pattern(this.vars.name_Pattern_Regex), Validators.minLength(3)]);
  emailControl = new FormControl('', [Validators.required, Validators.pattern(this.vars.email_Pattern_Regex)]);
  mobileControl = new FormControl('', Validators.required);
  info = AMOUNT_FORM;
  extn = '';
  oneTapOtpPopup: boolean;
  lives_count;
  exist_sip = [];
  cartData: any;
  userData: IUser;

  inputFieldData = {
    email: '',
    phone: ''
  };
  selectedCountryData;

  @ViewChild(GCaptchaComponent, { static: true }) gCaptchaComponent: GCaptchaComponent;

  constructor(
    public dialog: MatDialog,
    public util: UtilService,
    private paymentV2: PaymentV2Service,
    private auth: AuthService,
    public vwo: VwoService,
    public events: EventsService
  ) { }

  ngOnInit() {
    if (this.vars.isAutoLogin) {
      this.updateValidation('email', true);
    }
    this.vars.selected_insurance = null;
    this.userData = this.vars.userData.getValue();
    this.initForm();
    this.vars.trucallerRes.subscribe((res) => {
      this.donationForm.patchValue({
        name: res.name,
        email: res.email,
        mobile: res.phone
      });
    });
    this.vars.userData.subscribe(u => {
      if (u) {
        if (!this.vars?.isDummyEmail) {
          this.nameControl.setValue(u?.full_name || '');
          this.emailControl.setValue(u?.email || '');
        }
        if (u?.phone_1) {
          this.mobileControl.setValue(u?.phone_1);
        }
      }
    });

    this.subscribeInputChange();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.config?.amount && this.config?.paymentDetails?.default_amount && (changes?.config?.currentValue?.amount !== changes?.config?.previousValue?.amount || changes?.config?.currentValue?.sip_cause)) {
      this.getChildCount(this.config?.amount * this.util.getLengthToMultiply(this.config?.sip_cause), this.config?.paymentDetails?.default_amount);
    }
  }

  initForm() {
    if (this.userData) {
      this.nameControl.setValue(this.userData?.full_name || '');
      this.emailControl.setValue(this.userData?.email || '');
      this.mobileControl.setValue(this.userData?.phone_1 || '');
    }
    this.donationForm = new FormGroup({
      name: this.nameControl,
      email: this.emailControl,
      mobile: this.mobileControl,
    });
    if (this.vars?.isDummyEmail) {
      this.donationForm.patchValue({
        name: '',
        email: ''
      });
    }
    if (this.vars.isAgent) {
      this.donationForm.get('name').disable();
      this.donationForm.get('email').clearValidators();
      this.donationForm.get('email').addValidators([Validators.required]);
      this.donationForm.get('email').updateValueAndValidity();
    }
    this.paymentV2.paymentConfig.next({ labelAmount: null });
  }

  subscribeInputChange() {
    this.emailControl.valueChanges.subscribe(val => {
      if (this.inputFieldData.email === val) return;
      this.inputFieldData.email = val;
      if (this.vars.isAutoLogin) {
        const validatorKeys = this.util.getValidatorKeys(this.emailControl);
        if (val === this.userData?.email) {
          if (validatorKeys?.includes('pattern')) {
            this.updateValidation('email', true);
          }
        } else if (!validatorKeys?.includes('pattern')) {
          this.updateValidation('email', true, this.vars.email_Pattern_Regex);
        }
      }
    });
    this.mobileControl.valueChanges.subscribe(val => {
      if (this.inputFieldData.phone === val) return;
      this.inputFieldData.phone = val;
      if (this.vars.isAutoLogin) {
        const validatorKeys = this.util.getValidatorKeys(this.mobileControl);
        if (val === this.userData?.phone_1) {
          if (validatorKeys?.includes('pattern')) {
            this.updateValidation('phone', true);
          }
        } else if (!validatorKeys?.includes('pattern')) {
          this.updateValidation('phone', true, this.selectedCountryData?.pattern);
        }
      }
    });
  }


  /**Get the child count */
  getChildCount(amount, default_amount) {
    if (amount && default_amount) {
      const children = Math.ceil(amount / default_amount) * 12;
      this.lives_count = children > 36 ? 36 : children;
    }
  }

  /** OTP varification for one tap payment */
  otpVerified = (isVarified) => {
    this.oneTapOtpPopup = isVarified;
    if (isVarified) {
      this.paymentV2.otpVerified = isVarified;
      this.createCart();
    }
  }

  submitPayment() {
    if (this.config.amount >= this.config.paymentDetails?.min_donation) {
      if (this.util.checkNestedObj(this.config, 'lastOrder.payment_gateway') &&
        this.config.lastOrder.payment_gateway.match('stripe') &&
        !this.oneTapOtpPopup &&
        this.config.currency !== 'INR'
      ) {
        this.oneTapOtpPopup = true;
        return;
      } else if (this.config.currency !== 'INR' && !this.paymentV2.captchaResponse && !this.paymentV2.otpVerified) {
        this.gCaptchaComponent.executeCaptcha();
        return;
      }
      !this.config.user || !this.config.user?.extension || !this.config.user?.phone_1 || this.vars.isDummyEmail ? this.submitForm() : this.createCart();
    }
  }

  submitForm() {
    if (this.donationForm.valid) {
      const payload = {
        full_name: this.donationForm.get('name').value,
        phone_1: this.donationForm.get('mobile').value,
        email: this.donationForm.get('email').value,
        extension: this.extn
      };
      this.vars.userPhoneExt = payload.extension;
      this.vars.userPhoneNumber = payload.phone_1;
      if (this.userData) {
        this.createCart(payload);
      } else {
        this.checkForExistingUser(payload);
      }
    } else {
      this.util.checkValidForm(this.donationForm);
    }
  }

  async checkForExistingUser(payload) {
    this.paymentV2.isOrderLoading = true;
    try {
      const user = await this.auth.userLoginWithEmail(payload);
      this.paymentV2.paymentConfig.next({ user: user });
      await this.paymentV2.setUserLastOrder(user, this.config.paymentOptions.options);
      if (this.config.lastOrder && this.config.lastOrder.payment_gateway.match('stripe') && this.config.currency !== 'INR') {
        this.paymentV2.isOrderLoading = false;
        return;
      }
      this.createCart();
    } catch (error) {
      this.createCart(payload);
    }
  }

  async createCart(user?) {
    if (this.config.amount >= this.config.paymentDetails.min_donation) {
      if (typeof this.config.sip_cause === 'string') {
        this.config.sip_cause = [{ sip_cause: this.config.sip_cause, amount: this.config.amount }];
      } else if (!this.config.sip_cause) {
        this.config.sip_cause = [{ sip_cause: this.vars.default_sip_cause, amount: this.config.amount }];
      }
      const total_sip = this.paymentV2.totalSip(this.config.sip_cause, this.config);
      if (!total_sip.is_valid) { return; }
      const user_data = this.util.getUserData();
      this.exist_sip = Object.keys(user_data?.listsubscriptions || {});
      if (total_sip.only_cause_list.includes('sip_protect') && !total_sip.only_cause_list.includes('medical') && !this.exist_sip.includes('medical')) {
        return;
      }
      const cart_amount = total_sip?.total_amount || this.config.amount;
      let skipmin = false;
      if (cart_amount >= 1) {
        skipmin = true
      }
      const cartPayload = {
        campaign_id: this.config.paymentDetails.campaign.id,
        amount: cart_amount,
        currency: this.config.paymentDetails.selected_currency,
        is_anonymous: this.config.paymentDetails.is_anonymous,
        sip_cause: this.config.sip_cause,
        only_cause_list: total_sip.only_cause_list,
        user: user,
        sip_story: this.config.sip_story,
        skipmin: skipmin,
        recurring: !this.config.wallet_recharge ? 1 : null
      };

      if (this.config?.enableMaintenanceFee && this.config.paymentDetails?.mandate_fee) {
        cartPayload.amount += this.config.paymentDetails.mandate_fee;
      }

      if (this.config.enable_multicause_slection) {
        cartPayload['multicause'] = true;
      }

      if (this.config.sip_story) {
        cartPayload['transaction_type'] = null;
      } else if (this.config.sip_wallet) {
        cartPayload['transaction_type'] = `wallet_new_sip_${this.config.wallet_tenure}`;
      } else if (this.config.auto_pay_m1) {
        cartPayload['transaction_type'] = 'new_sip_amt_1';
      }

      if (this.config.enable_multicause_slection) {
        cartPayload['multicause'] = true;
      }

      if (this.config?.enableMaintenanceFee && this.config.paymentDetails?.mandate_fee) {
        cartPayload['tip_amount'] = this.config.paymentDetails.mandate_fee;
      }

      this.cartData = await this.paymentV2.createCart(cartPayload);
      this.paymentV2.paymentConfig.next({ cart: this.cartData.cart, fundraiser: this.cartData.fundraiser });
      if (
        this.util.checkNestedObj(this.config, 'lastOrder.payment_gateway') &&
        this.config.lastOrder.payment_gateway.match('stripe') &&
        this.config.currency !== 'INR'
      ) {
        this.oneTapCardPayment();
        return;
      }
      if (!this.config.redirectTo) {
        this.formSubmitted.emit(true);
      }
      const params = this.util.getUrlParams();
      if (params?.utm_source?.match('internal_impact_calling') && this.vars.varLoginData.userdata.email !== this.cartData?.cart?.donor_email) {
        this.util.openSnackBar('User login error, please reopen in fresh incognito window', 'error');
        this.events.sendSystemEvent({
          eventName: 'user_missmatch',
          page_name: this.vars.pageName,
          info_1: this.vars.varLoginData.userdata.email,
          info_2: this.cartData?.cart?.donor_email,
        }).subscribe(_ => _);
        return;
      }
      this.redirectToPaymentPage();
    }
  }

  oneTapCardPayment() {
    this.dataEmit.emit({
      mode: 'CARD',
      card_name_fill: this.config.user.full_name,
      mode_value: this.config.lastOrder.payment_mode_value,
      customer_id: this.config.lastOrder.payment_details.customer_id
    });
    return true;
  }

  redirectToPaymentPage() {
    this.paymentV2.isOrderLoading = false;
    if (this.config.redirectTo) {
      let redirectTo = '';
      const queryParams = {
        amt: this.config.cart_amount,
        currency: this.config.currency,
        campaign_id: this.cartData?.cart?.campaign_id || this.config.paymentDetails.campaign.id,
        is_multicause: this.config.enable_multicause_slection || null,
        payment_method_change: this.config.payment_method_change || null,
        min_donation: this.config.paymentDetails.min_donation
      };
      if (this.config.sip_cause) {
        queryParams['sip_cause'] = this.util.jsonToString(this.config.sip_cause);
      }
      if (this.config.frequency_term) {
        queryParams['frequency'] = this.config.frequency;
      }
      if (this.config.redirectTo === 'welcome') {
        queryParams['last_pay_mode'] = this.config.lastOrder.payment_mode;
        redirectTo = '/new/sip/welcome';
      } else if (this.config.redirectTo === 'standalone') {
        redirectTo = '/new/sip/payment-standalone';
        if (!this.redirectErrorCheck('standalone')) {
          return false;
        }
      }
      this.util.router.navigate([redirectTo], { queryParams: queryParams, queryParamsHandling: 'merge' });
      this.dialog.closeAll();
    } else {
      this.util.router.navigate([], { queryParams: { page: 'op' }, queryParamsHandling: 'merge' });
    }
  }

  emitConfig(amount) {
    this.paymentV2.paymentConfig.next({ amount: amount });
    if (typeof this.config?.sip_cause === 'object') {
      this.config?.sip_cause.forEach((element, i) => {
        this.config.sip_cause[i] = { sip_cause: element?.sip_cause, amount: amount }
      });
    }
  }

  captchaResponse(e) {
    this.paymentV2.captchaResponse = e;
    this.submitPayment();
  }

  setFrequency(e) {
    this.paymentV2.paymentConfig.next({ frequency: e });
  }

  redirectErrorCheck(to) {
    const notSupported = this.config?.sip_cause?.filter(val => ['elderly', 'women_girls'].includes(val.sip_cause))[0];
    if (to === 'standalone' && notSupported && this.config.currency !== 'INR') {
      this.util.openSnackBar('The selected cause only supports INR monthly contributions', 'error');
      this.paymentV2.isOrderLoading = false;
      return false;
    }
    return true;
  }

  setCountry(e) {
    this.selectedCountryData = e;
    this.extn = e?.dialCode;
    if (this.vars.isAutoLogin && e?.pattern?.test(this.mobileControl.value.replaceAll('*', '1'))) {
      this.updateValidation('phone', true);
    } else {
      this.updateValidation('phone', true, e?.pattern);
    }
  }

  onFocusOut(field: 'email' | 'phone') {
    if (field === 'email' && this.vars.isAutoLogin) {
      if (this.emailControl.value === this.userData.email) {
        this.updateValidation('email', true);
      } else {
        this.updateValidation('email', true, this.vars.email_Pattern_Regex);
      }
    } else if (field === 'phone' && this.vars.isAutoLogin) {
      if (this.mobileControl.value === this.userData?.phone_1) {
        this.updateValidation('phone', true);
      } else {
        this.updateValidation('phone', true, this.selectedCountryData?.pattern);
      }
    }
  }

  updateValidation(field: 'email' | 'phone', require: boolean, pattern?: any) {
    const validators = [];
    if (require) validators.push(Validators.required);
    if (pattern) validators.push(Validators.pattern(pattern));
    if (field === 'email') {
      this.emailControl.clearValidators();
      this.emailControl.addValidators(validators);
      this.emailControl.updateValueAndValidity();
    } else if (field === 'phone') {
      this.mobileControl.clearValidators();
      this.mobileControl.addValidators(validators);
      this.mobileControl.updateValueAndValidity();
    }
  }
}
